import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { AdminConfigService } from './admin-config.service';

export interface AdminUser {
  email: string;
  name: string;
  roles: string[];
}

export interface AuthResponse {
  verified: boolean;
  token: string;
  user: AdminUser;
}

/** All roles that can be simulated in "View As" mode */
export const AVAILABLE_ROLES = ['OPS', 'DEVELOPER', 'SUPPORT', 'CONTENT'] as const;
export type AdminRole = (typeof AVAILABLE_ROLES)[number];

@Injectable({
  providedIn: 'root'
})
export class AdminAuthService {
  private readonly config = inject(AdminConfigService);
  private readonly router = inject(Router);

  // State
  readonly currentUser = signal<AdminUser | null>(this.loadUserFromStorage());
  readonly isAuthenticated = computed(() => !!this.currentUser());
  readonly token = signal<string | null>(localStorage.getItem('admin_token'));

  // "View As" role simulation (only for SUPER_ADMIN)
  readonly viewAsRole = signal<AdminRole | null>(null);

  /** True if the actual user (not simulated) is a SUPER_ADMIN */
  readonly isSuperAdmin = computed(() => {
    const user = this.currentUser();
    return user?.roles.includes('SUPER_ADMIN') ?? false;
  });

  /** Returns simulated role array if active, otherwise the actual user roles */
  readonly effectiveRoles = computed(() => {
    const simulatedRole = this.viewAsRole();
    if (simulatedRole && this.isSuperAdmin()) {
      return [simulatedRole];
    }
    return this.currentUser()?.roles ?? [];
  });

  constructor() {
    const storedToken = this.token();
    console.log('[AdminAuth] Initializing service. Token present:', !!storedToken);
    // Restore session if token exists (verify with server)
    if (storedToken) {
      this.restoreSession();
    }
  }

  private loadUserFromStorage(): AdminUser | null {
    try {
      const stored = localStorage.getItem('admin_user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  private get baseUrl() {
    return this.config.baseUrl();
  }

  /**
   * Login Flow (Assertion)
   */
  async login(email: string): Promise<void> {
    try {
      // 1. Get Challenge
      const resp = await fetch(`${this.baseUrl}/auth/login/challenge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.error || 'Failed to get login challenge');
      }

      const options = await resp.json();
      console.log('[AdminAuth] Login Options:', options);

      // 2. Sign with Passkey
      let authResponse;
      try {
        // Race with timeout to prevent hanging if extension/browser swallows the promise
        // Increased to 60s to allow user interaction time
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Authentication timed out')), 60000)
        );

        authResponse = await Promise.race([
          startAuthentication({ optionsJSON: options }),
          timeoutPromise
        ]);

        console.log('[AdminAuth] Passkey signed:', authResponse);
      } catch (webAuthnError: any) {
        console.error('[AdminAuth] startAuthentication failed:', webAuthnError);
        throw new Error(`Passkey interaction failed: ${webAuthnError.name} - ${webAuthnError.message}`);
      }

      // 3. Verify
      const verifyResp = await fetch(`${this.baseUrl}/auth/login/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, response: authResponse }),
      });

      if (!verifyResp.ok) {
        const err = await verifyResp.json();
        throw new Error(err.error || 'Verification failed');
      }

      const result: AuthResponse = await verifyResp.json();
      this.handleAuthSuccess(result);

    } catch (error) {
      console.error('Login failed', error);
      throw error;
    }
  }

  /**
   * Registration Flow (Attestation)
   * Requires an invite token (initial setup) or an authenticated session (adding device)
   */
  async register(email: string, token?: string): Promise<void> {
    try {
      // 1. Get Challenge
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (this.token()) {
        headers['Authorization'] = `Bearer ${this.token()}`;
      }

      const resp = await fetch(`${this.baseUrl}/auth/register/challenge`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email, token }),
      });

      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.error || 'Failed to get registration challenge');
      }

      const options = await resp.json();
      console.log('[AdminAuth] Register Options:', options);

      // 2. Create Passkey
      let regResponse;
      try {
        regResponse = await startRegistration({ optionsJSON: options });
        console.log('[AdminAuth] Passkey registered on device:', regResponse);
      } catch (webAuthnError: any) {
        console.error('[AdminAuth] startRegistration failed:', webAuthnError);
        throw new Error(`Passkey creation failed: ${webAuthnError.name} - ${webAuthnError.message}`);
      }

      // 3. Verify
      const verifyResp = await fetch(`${this.baseUrl}/auth/register/verify`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email, response: regResponse }),
      });

      if (!verifyResp.ok) {
        const err = await verifyResp.json();
        throw new Error(err.error || 'Registration verification failed');
      }

      // Registration successful

    } catch (error) {
      console.error('Registration failed', error);
      throw error;
    }
  }

  logout(): void {
    console.log('[AdminAuth] logout() called - clearing token');
    console.trace('[AdminAuth] logout call stack');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    this.token.set(null);
    this.currentUser.set(null);
    this.viewAsRole.set(null); // Clear simulation on logout
    this.router.navigate(['/login']);
  }

  hasRole(role: string): boolean {
    const user = this.currentUser();
    if (!user) return false;

    // If simulating, use effective roles (simulated role only)
    const simulatedRole = this.viewAsRole();
    if (simulatedRole && this.isSuperAdmin()) {
      return simulatedRole === role;
    }

    // Normal path: SUPER_ADMIN has all roles
    if (user.roles.includes('SUPER_ADMIN')) return true;
    return user.roles.includes(role);
  }

  /** Set a role to simulate (only works for SUPER_ADMIN) */
  setViewAsRole(role: AdminRole | null): void {
    if (this.isSuperAdmin()) {
      this.viewAsRole.set(role);
    }
  }

  /** Exit "View As" mode */
  clearViewAs(): void {
    this.viewAsRole.set(null);
  }

  private handleAuthSuccess(result: AuthResponse): void {
    localStorage.setItem('admin_token', result.token);
    localStorage.setItem('admin_user', JSON.stringify(result.user));
    this.token.set(result.token);
    this.currentUser.set(result.user);
    this.router.navigate(['/']);
  }

  private async restoreSession(): Promise<void> {
    try {
      const resp = await fetch(`${this.baseUrl}/auth/me`, {
        headers: { 'Authorization': `Bearer ${this.token()}` }
      });

      if (resp.ok) {
        const data = await resp.json();
        this.currentUser.set(data.user);
        localStorage.setItem('admin_user', JSON.stringify(data.user));
      } else if (resp.status === 401 || resp.status === 403) {
        // Token invalid/expired
        const err = await resp.json().catch(() => ({ error: 'Unknown' }));
        console.warn('Session expired, logging out. Status:', resp.status, 'Error:', err);
        this.logout();
      } else {
        console.error('Failed to restore session:', resp.status, resp.statusText);
        // Do not logout for other errors (e.g. 500 or CORS if caught below)
      }
    } catch (e) {
      console.error('Network error restoring session:', e);
      // Do not logout on network errors (including CORS if fetch throws)
    }
  }
}